import { CONFIG } from '../config/index.js';
import { Particle, Effect } from '../entities/index.js';

export class MetaProgressionSystem {
    constructor(game) {
        this.game = game;
        // Carica nodi sbloccati dai salvataggi se esistono, altrimenti vuoto (o converte permanentUpgrades vecchi)
        this.unlockedNodes = this.game.loadGameData?.()?.skillTreeNodes || {};

        // Mantieni vecchio sistema di compatibilità se `skillTreeNodes` è vuoto ma `permanentUpgrades` esiste
        if (Object.keys(this.unlockedNodes).length === 0 && this.game.permanentUpgrades) {
            // Un semplice mapping potrebbe essere fatto qui se necessario
        }
    }

    getUnlockedLevel(nodeId) {
        return this.unlockedNodes[nodeId] || 0;
    }

    isNodeUnlocked(nodeId) {
        return this.getUnlockedLevel(nodeId) > 0;
    }

    canUnlockNode(nodeId) {
        const nodeData = CONFIG.skillTree ? CONFIG.skillTree[nodeId] : null;
        if (!nodeData) return false;

        // Se è già al livello massimo non si può sbloccare oltre
        if (this.getUnlockedLevel(nodeId) >= nodeData.maxLevel) return false;

        // Se non ha requisiti ed è il nodo base, si può sbloccare
        if (!nodeData.requires || nodeData.requires.length === 0) return true;

        const requireType = nodeData.requireType || 'any'; // 'any' (or) o 'all' (and)

        if (requireType === 'all') {
            return nodeData.requires.every(reqId => this.isNodeUnlocked(reqId));
        } else {
            return nodeData.requires.some(reqId => this.isNodeUnlocked(reqId));
        }
    }

    getNodeCost(nodeId) {
        const nodeData = CONFIG.skillTree ? CONFIG.skillTree[nodeId] : null;
        if (!nodeData) return Infinity;
        const currentLevel = this.getUnlockedLevel(nodeId);
        // Costo cresce del 30% per livello
        return Math.floor(nodeData.baseCost * Math.pow(1.3, currentLevel));
    }

    unlockNode(nodeId) {
        if (!this.canUnlockNode(nodeId)) return false;

        const cost = this.getNodeCost(nodeId);
        if (this.game.totalGems < cost) return false;

        this.game.totalGems -= cost;
        this.unlockedNodes[nodeId] = (this.unlockedNodes[nodeId] || 0) + 1;

        // Assicurati che il save data rifletta il cambiamento
        this.saveData();
        return true;
    }

    saveData() {
        if (this.game.saveGameData) {
            this.game.saveGameData();
        } else if (this.game.saveData) {
            this.game.saveData(); // metodo fallibile in base a com'è scritto
        }
    }

    // Applica logicamente i modificatori allo starting player (chiamato all'inizio della run)
    applyModifiersToPlayer(player) {
        // Applichiamo i bonus solo dai nodi skillTree
        for (const [nodeId, level] of Object.entries(this.unlockedNodes)) {
            const nodeData = CONFIG.skillTree ? CONFIG.skillTree[nodeId] : null;
            if (!nodeData || level === 0 || !nodeData.effect) continue;

            const effect = nodeData.effect;

            if (effect.type === 'stat') {
                if (effect.stat === 'luck_and_xp') {
                    player.modifiers.luck += effect.value * level;
                    player.modifiers.xpGain += effect.value * level;
                } else if (['maxHp', 'speed', 'dr'].includes(effect.stat)) {
                    // Statistiche assolute
                    player.stats[effect.stat] += effect.value * level;
                    if (effect.stat === 'maxHp') player.hp = player.stats.maxHp; // Cura il player al nuovo max
                } else {
                    // Statistiche relative (moltiplicatori)
                    player.modifiers[effect.stat] += effect.value * level;
                }
            }
        }
    }

    // Usato dal Game Loop per effetti attivi passivi (es. rigenerazione, meteorite)
    update(deltaTime) {
        const player = this.game.player;
        if (!player || player.hp <= 0) return;

        // Regen
        if (this.unlockedNodes['tier1_regen']) {
            const regenAmount = 1 * this.unlockedNodes['tier1_regen'];
            this.regenTimer = (this.regenTimer || 0) + deltaTime;
            if (this.regenTimer >= 5000) { // Ogni 5 secondi
                player.heal(regenAmount);
                this.regenTimer = 0;
            }
        }
    }

    // Usato da altri sistemi per comunicare eventi (es. kill)
    onEnemyKilled(enemy) {
        // Logica meteorite speciale
        if (this.unlockedNodes['special_meteor']) {
            this.meteorKillCount = (this.meteorKillCount || 0) + 1;
            if (this.meteorKillCount >= 100) {
                this.meteorKillCount = 0;
                this.spawnMeteor();
            }
        }
    }

    spawnMeteor() {
        if (!this.game.entities || !this.game.player) return;

        const enemies = this.game.getEnemiesAndBosses?.() || [];
        let targetX = this.game.player.x;
        let targetY = this.game.player.y;

        if (enemies.length > 0) {
            const idx = Math.floor(Math.random() * enemies.length);
            targetX = enemies[idx].x;
            targetY = enemies[idx].y;
        }

        const explosionRadius = 80 * (this.game.player.modifiers?.area ?? 1);
        const damage = this.game.getDamage?.(500) ?? 500 * (this.game.player?.modifiers?.power ?? 1);

        this.game.addEntity('effects', new Effect(targetX, targetY, { type: 'meteor_indicator', radius: explosionRadius, life: 45, initialLife: 45 }));
        setTimeout(() => {
            if (this.game.createExplosion) {
                this.game.createExplosion(targetX, targetY, explosionRadius, damage);
            }
            for (let k = 0; k < 15; k++) {
                this.game.addEntity('particles', new Particle(targetX, targetY, { vx: (Math.random() - 0.5) * 10, vy: (Math.random() - 0.5) * 10, life: 40, color: '#ff4500' }));
            }
        }, 750);
    }

    onPlayerDeath(player) {
        // Logica revive
        if (this.unlockedNodes['special_second_chance'] && !this.hasRevivedThisRun) {
            this.hasRevivedThisRun = true;
            player.hp = player.stats.maxHp * 0.5;
            this.game.notifications?.push({ text: 'ANIMA PERSISTENTE: Hai ottenuto una seconda possibilità!', life: 200, color: '#ffd700' });
            return true; // Blocca la morte
        }
        return false; // Continua a morire
    }

    resetRunState() {
        this.meteorKillCount = 0;
        this.hasRevivedThisRun = false;
        this.regenTimer = 0;
    }
}
